import { Button } from '@sjuni/ui';
import { Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { type Column, ResourceTable } from '@/components/list/ResourceTable';
import { ResourceToolbar } from '@/components/list/ResourceToolbar';
import { useResourceAction, useResourceList } from '@/lib/api/resource';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';

import { VocationalContactDialog } from './VocationalContactDialog';
import type { VocationalContactRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/vocational/contacts`, and
 * docs/01-INFORMATION-ARCHITECTURE.md §13.7's four coordinators.
 *
 * The first screen built on the §4.6 scaffolding, and small enough to be a
 * fair test of it: a list, a form with two translatable fields, and a delete.
 *
 * The delete is permanent. `vocational_contacts` is the one table in
 * docs/03-DATA-MODEL.md §5 without a `deleted_at`, which is right for rows
 * holding a named person's phone number — so the confirmation says so rather
 * than promising a restore that does not exist.
 */
export function VocationalContactsPage() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const list = useListParams('sort_order');

  const [editing, setEditing] = useState<VocationalContactRow | 'new' | null>(null);
  const [deleting, setDeleting] = useState<VocationalContactRow | null>(null);

  const { data, isPending, isError } = useResourceList<VocationalContactRow>(
    'vocational-contacts',
    list.params,
  );

  const remove = useResourceAction<VocationalContactRow>('vocational-contacts');

  const columns: Column<VocationalContactRow>[] = [
    {
      key: 'role',
      label: t('vocational.contacts.columns.role'),
      render: (row) => (
        <span className="font-semibold text-primary">{valueFor(row.role, locale)}</span>
      ),
    },
    {
      key: 'person_name',
      label: t('vocational.contacts.columns.person'),
      render: (row) => valueFor(row.person_name, locale),
    },
    {
      key: 'phone',
      label: t('vocational.contacts.columns.phone'),
      // Not a `tel:` link: this is a management list, and a stray click on a
      // desktop opens whatever the OS thinks handles calls.
      render: (row) => row.phone ?? '—',
    },
    {
      key: 'email',
      label: t('vocational.contacts.columns.email'),
      render: (row) => row.email ?? '—',
    },
    {
      key: 'sort_order',
      label: t('vocational.contacts.columns.order'),
      sortable: true,
      align: 'end',
      render: (row) => row.sort_order,
    },
    {
      key: 'actions',
      label: t('list.actions'),
      align: 'end',
      render: (row) => (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          iconLeft={<Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />}
          onClick={(event) => {
            // The row itself opens the editor on click, and a delete button
            // inside it must not do both.
            event.stopPropagation();
            setDeleting(row);
          }}
        >
          {t('common.delete')}
        </Button>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.vocationalContacts')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('vocational.contacts.intro')}</p>
      </div>

      {isError ? <FormError message={t('errors.generic')} /> : null}

      <ResourceToolbar
        search={list.search}
        onSearchChange={list.setSearch}
        perPage={list.perPage}
        onPerPageChange={list.setPerPage}
        showTrashed={list.showTrashed}
        onShowTrashedChange={list.setShowTrashed}
        meta={data?.meta}
        buildPageHref={list.buildPageHref}
        primaryAction={
          <Button
            type="button"
            size="sm"
            iconLeft={<Plus size={16} strokeWidth={1.75} aria-hidden="true" />}
            onClick={() => setEditing('new')}
          >
            {t('vocational.contacts.add')}
          </Button>
        }
      />

      <ResourceTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={isPending}
        sort={list.sort}
        onSortChange={list.setSort}
        onOpen={setEditing}
        emptyTitle={t('vocational.contacts.empty.title')}
        emptyBody={t('vocational.contacts.empty.body')}
        emptyAction={
          <Button type="button" size="sm" onClick={() => setEditing('new')}>
            {t('vocational.contacts.add')}
          </Button>
        }
      />

      {editing ? (
        <VocationalContactDialog
          contact={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title={t('vocational.contacts.delete.title')}
        body={t('vocational.contacts.delete.body', {
          name: deleting
            ? valueFor(deleting.person_name, locale) || valueFor(deleting.role, locale)
            : '',
        })}
        confirmLabel={t('common.delete')}
        // §5's schema gives this table no `deleted_at`, so there is nothing to
        // restore afterwards and the dialog must not imply otherwise.
        reversible={false}
        busy={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) {
            remove.mutate(
              { id: deleting.id, action: 'delete' },
              { onSettled: () => setDeleting(null) },
            );
          }
        }}
      />

      {list.showTrashed ? (
        <p className="flex items-center gap-2 text-caption text-muted">
          <RotateCcw size={14} strokeWidth={1.75} aria-hidden="true" />
          {t('vocational.contacts.noTrash')}
        </p>
      ) : null}
    </div>
  );
}
